/**
 * Which Evaluwhale this is, as shown in the application chrome.
 *
 * The number comes from the backend at runtime rather than from a constant
 * baked in at build time. That is deliberate: the SPA and the API are two
 * images that can be deployed independently, and the version worth showing a
 * scientist is the one belonging to the process that computed the numbers. A
 * build-time constant would show the SPA's version and quietly claim it was
 * both.
 *
 * There is one version for the whole product — see ADR-0014 and
 * `docs/releases.md`.
 */

/**
 * The version in an `/api/info` body, or `null` if there isn't one.
 *
 * `analysis_version` is the fallback because it is the older name and is what a
 * backend predating ADR-0014 serves. They are the same number by construction.
 *
 * Returning `null` rather than a placeholder is the point of this function:
 * when the API cannot be reached, the header shows nothing at all. A version is
 * a provenance claim, and there is no honest way to make one about a backend
 * that has not answered.
 */
export function versionFrom(info) {
  if (!info || typeof info !== "object") return null;
  const value = info.version ?? info.analysis_version;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** How the version reads in the chrome: `v0.4.0`, or nothing. */
export function versionLabel(info) {
  const version = versionFrom(info);
  return version ? `v${version}` : null;
}

/**
 * What the version links to.
 *
 * The release-notes anchor in the documentation site. The id is derived by the
 * site generator from the document's path (`docs/releases.md`), so this string
 * and `docIdFor()` in `scripts/build-docs.mjs` have to agree; the anchor is
 * asserted against the generated site by `npm run docs` followed by a grep, not
 * by a test that could pass while the site says otherwise.
 */
export const RELEASE_NOTES_URL = "/docs/#docs-releases";
